import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Images, Play, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { imageBackfillAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

export default function CatalogImages() {
  useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const pollRef = useRef(null);

  const load = useCallback(() => imageBackfillAPI.status()
    .then(r => setData(r.data.data || r.data))
    .catch(e => setErr(e.response?.data?.message || 'Failed to load status')), []);
  useEffect(() => { load(); }, [load]);

  const running = data?.status?.running;
  useEffect(() => {
    if (running && !pollRef.current) pollRef.current = setInterval(load, 3000);
    else if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [running, load]);

  const run = () => {
    setBusy(true); setMsg(''); setErr('');
    imageBackfillAPI.run()
      .then(r => { setMsg(r.data.message || 'Started.'); setTimeout(load, 1500); })
      .catch(e => setErr(e.response?.data?.message || 'Could not start'))
      .finally(() => setBusy(false));
  };

  if (!data && err) return <div style={{ padding: 24 }}><div className="card" style={{ padding: 24, borderLeft: `4px solid ${RED}`, color: RED }}>{err}</div></div>;
  if (!data) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading…</div>;

  const s = data.status || {};
  const totals = data.totals || {};
  const pct = s.total ? Math.round((s.processed / s.total) * 100) : 0;
  const done = !s.running && s.finishedAt;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
          <Images size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />Catalog Images
        </h1>
        <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500, maxWidth: 860, lineHeight: 1.55 }}>
          Fills in missing product photos across the catalog. For every item whose image is missing or broken, it fetches a
          real product photo from web image search and uploads it to your S3 bucket. Items that already have a working image are skipped.
        </p>
      </div>

      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', marginBottom: s.total ? 20 : 0 }}>
          <Stat label="Catalog items" value={totals.items ?? '—'} />
          <Stat label="Missing image field" value={totals.withoutImageField ?? '—'} color={GOLD} />
          <div style={{ flex: 1 }} />
          <button onClick={run} disabled={busy || s.running} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
            background: s.running ? GOLD : GREEN, color: '#fff', fontSize: 14, fontWeight: 700, opacity: busy ? 0.6 : 1,
          }}>
            {s.running ? <RefreshCw size={16} className="spin" /> : <Play size={16} />}
            {s.running ? 'Backfilling…' : 'Backfill missing images'}
          </button>
        </div>
        <style>{`.spin{animation:spin 1.1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>

        {(s.running || done) && (
          <div>
            <div style={{ height: 12, background: '#eef2f0', borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${GREEN}, ${GREEN}cc)`, borderRadius: 6, transition: 'width .5s ease' }} />
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, fontWeight: 600 }}>
              <span style={{ color: CHAR }}>{s.processed}/{s.total} processed ({pct}%)</span>
              <span style={{ color: GREEN }}>✓ {s.filled} filled</span>
              <span style={{ color: MUTED }}>↷ {s.skipped} already had images</span>
              <span style={{ color: s.failed ? RED : MUTED }}>✗ {s.failed} no photo found</span>
              {s.running && s.current && <span style={{ color: PURPLE }}>→ {s.current}</span>}
            </div>
            {done && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: GREEN }}>
                <CheckCircle size={16} />Done — {s.filled} images added. Re-run anytime to fill any that failed (web search can miss some).
              </div>
            )}
            {s.lastError && <div style={{ marginTop: 8, fontSize: 12, color: RED }}>last error: {s.lastError}</div>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 10, background: 'rgba(245,166,35,0.10)', border: `1px solid ${GOLD}`, fontSize: 13, color: CHAR, fontWeight: 500, lineHeight: 1.55 }}>
        <AlertTriangle size={17} style={{ color: GOLD, flexShrink: 0, marginTop: 1 }} />
        <span>Runs in the background — you can leave this page; progress resumes here. Web-search photos are great for branded products and rougher for services. It never overwrites an item that already has a working image, so it's safe to run repeatedly.</span>
      </div>
    </div>
  );
}

function Stat({ label, value, color = GREEN }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
